import { Button, IconButton } from 'src/components/atoms';
import { ErrorMessage } from 'src/components/atoms/FormErrorMessage';
import styled from 'styled-components';

export const AnswerOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

export const AnswerOptionsLabel = styled.div`
  font-family: ${(props) =>
    props.theme.atlas.typography.semantic.caption.fontFamily};
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.3px;
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
`;

export const RequiredMark = styled.span`
  font-weight: 400;
`;

export const EmptyStateContainer = styled.div<{ $hasError?: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  width: 100%;
  min-height: 104px;
  border: 1px solid
    ${(props) =>
      props.$hasError
        ? props.theme.atlas.semantic.color.action.destructive.hover
        : '#e6e6e6'};
  border-radius: 12px;
  background-color: ${(props) =>
    props.$hasError
      ? props.theme.atlas.semantic.color.status.error.background
      : props.theme.atlas.semantic.color.surface.default};
`;

export const EmptyStateText = styled.p<{ $hasError?: boolean }>`
  font-family: ${(props) =>
    props.theme.atlas.typography.semantic.emphasis.fontFamily};
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.2px;
  color: ${(props) =>
    props.$hasError
      ? props.theme.atlas.semantic.color.status.error.text
      : props.theme.atlas.semantic.color.type.default};
  margin: 0;
  text-align: center;
`;

export const AddFirstOptionButton = styled(Button)`
  && {
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 8px;
    width: fit-content;
    min-width: 142px;
    height: 32px;
    padding: 4px 12px;
    white-space: nowrap;
    background-color: transparent;
    border: 1px solid #282e37;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: 0.2px;
    color: #282e37;
  }

  @media (hover: hover) {
    &:hover:not(:disabled) {
      background-color: ${(props) =>
        props.theme.atlas.semantic.color.action.secondary.hoverFill};
    }
  }
`;

export const AddOptionButton = styled(Button)`
  && {
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-width: 0;
    height: 32px;
    padding: 4px 12px;
    background-color: transparent;
    border: 1px solid #282e37;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: 0.2px;
    color: #282e37;
  }

  @media (hover: hover) {
    &:hover:not(:disabled) {
      background-color: ${(props) =>
        props.theme.atlas.semantic.color.action.secondary.hoverFill};
    }
  }

  &:disabled {
    border-color: #a0a2a5;
    color: #a0a2a5;
    cursor: default;

    i {
      color: #a0a2a5 !important;
    }
  }
`;

export const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const DataGridFlagIfAnsweredRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 24px;
`;

export const OptionBlock = styled.div<{
  $hasDivider?: boolean;
  $hideFlowControls?: boolean;
  $hasTopSpacing?: boolean;
}>`
  display: grid;
  grid-template-columns: ${(props) =>
    props.$hideFlowControls
      ? 'minmax(0, 1fr) auto'
      : 'minmax(0, 1fr) auto auto'};
  grid-template-rows: auto auto auto;
  column-gap: 8px;
  row-gap: 8px;
  width: 100%;
  margin-top: ${(props) => (props.$hasTopSpacing ? '16px' : '0')};
  padding-bottom: ${(props) => (props.$hasDivider ? '16px' : '0')};
  border-bottom: ${(props) =>
    props.$hasDivider ? '1px solid #dadada' : 'none'};
`;

export const OptionMetaRow = styled.div<{ $counterOnly?: boolean }>`
  grid-column: 1;
  grid-row: 1;
  display: flex;
  flex-direction: row;
  justify-content: ${(props) =>
    props.$counterOnly ? 'flex-end' : 'space-between'};
  align-items: center;
  gap: 8px;
  min-height: 24px;
  min-width: 0;
`;

export const OptionInputField = styled.div`
  grid-column: 1;
  grid-row: 2;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  align-self: center;
`;

export const FieldInputWrapper = styled.div`
  position: relative;
  width: 100%;

  input {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }
`;

export const OptionHelperText = styled.div`
  font-family: ${(props) =>
    props.theme.atlas.typography.semantic.body.fontFamily};
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.3px;
  color: #6f7377;
  width: 100%;
`;

export const OptionDependencyGroup = styled.div`
  grid-column: 2;
  grid-row: 2;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  align-self: center;
  height: 40px;
`;

export const FlowIconButton = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 4px;
  border-radius: 8px;
`;

export const OptionDropdownField = styled.div`
  flex: 0 0 210px;
  width: 210px;
  min-width: 0;
`;

export const OptionDeleteControl = styled.div<{ $hideFlowControls?: boolean }>`
  grid-column: ${(props) => (props.$hideFlowControls ? '2' : '3')};
  grid-row: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  width: 32px;
  height: 40px;
`;

export const DeleteIconButton = styled(IconButton)<{
  $isOtherOption?: boolean;
}>`
  cursor: pointer;

  i {
    color: ${(props) =>
      props.$isOtherOption ? '#6f7377' : '#a0a2a5'} !important;
  }

  &:not(:disabled) {
    cursor: pointer !important;

    button {
      cursor: pointer !important;
    }

    i {
      cursor: pointer !important;
    }
  }

  &:hover:not(:disabled) {
    cursor: pointer !important;
    background-color: transparent !important;
    opacity: 0.7;
  }

  &:active:not(:disabled) {
    opacity: 0.5;
  }

  &:disabled {
    background-color: transparent !important;
    border: none !important;
    cursor: default !important;
    opacity: 0.4;

    button {
      background-color: transparent !important;
      border: none !important;
      cursor: default !important;
    }

    i {
      cursor: default !important;
    }
  }
`;

export const MaxOptionsAlert = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-height: 56px;
  padding: 8px 16px;
  background: #ffedeb;
  border-radius: 12px;
`;

export const MaxOptionsAlertMessage = styled.p`
  font-family: ${(props) =>
    props.theme.atlas.typography.semantic.body.fontFamily};
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.2px;
  color: #921a1d;
  margin: 0;
  flex: 1;
  min-width: 0;
`;

export const OptionErrorRow = styled.div`
  grid-column: 1;
  grid-row: 3;
  display: flex;
  align-items: flex-start;
`;

export const OptionErrorContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

export const StyledErrorMessage = styled(ErrorMessage)`
  display: block;
`;

export const CharacterCounter = styled.span<{ isError?: boolean }>`
  font-family: ${(props) =>
    props.theme.atlas.typography.semantic.body.fontFamily};
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.3px;
  font-feature-settings:
    'tnum' on,
    'lnum' on;
  color: ${(props) =>
    props.isError
      ? props.theme.atlas.semantic.color.status.error.text
      : '#6f7377'};
  flex-shrink: 0;
  white-space: nowrap;
  text-align: right;
`;
